import { Fragment, useState } from "react";

import {
  ActionValue,
  DataViewKey,
  KpkBasket,
  dataViewPage,
  selectedElements,
  useBasketDelete,
  useBasketDownload,
  useBasketEdit,
  useBasketShare,
} from "@keepeek/commons";
import {
  BasketCard,
  BasketCardBackgroundSize,
  BasketDelete,
  BasketEdit,
  KButton,
  withSuspense,
} from "@keepeek/refront-components";
import { Box, alpha } from "@mui/material";
import * as Sentry from "@sentry/nextjs";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { KIcon } from "../../../../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../../../../components/layout/adminMenu";
import logger from "../../../../lib/logger-utils";
import { Basket } from "../../../../models/configuration/components/basket";
import { useConfiguration } from "../../../../providers/config/hooks";
import { configSectionComponentBasketSelector } from "../../../../providers/config/selectors";
import { PagesPath } from "../../../App/utils";
import BasketShare from "../../../Basket/BasketShare";
import { useBasketsWidgetBasketSelection } from "../hooks";

const BasketCardForWidget = function ({
  basket,
  widgetId,
}: {
  basket: KpkBasket;
  widgetId: string;
}) {
  const sentryCallback = ({ category, message }) => Sentry.addBreadcrumb({ category, message });
  const { enqueueSnackbar } = useSnackbar();
  const { isSelected, toggle } = useBasketsWidgetBasketSelection({ widgetId, basketId: basket.id });
  const setPage = useSetRecoilState(dataViewPage(DataViewKey.BASKET_DETAIL));

  const snackbarCallback = ({ message, options }) => enqueueSnackbar(message, options);

  const router = useRouter();
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const { deleteBasket } = useBasketDelete(basket.id, sentryCallback, snackbarCallback, () =>
    setOpenDelete(false),
  );
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [titleEdit, setTitleEdit] = useState<string>("");
  const { update } = useBasketEdit({
    basketId: basket.id,
    sentryCallback,
    snackbarCallback,
    updateCallback: () => setOpenEdit(false),
  });
  const { displayBackofficeSharedBasket } = useRecoilValue(
    configSectionComponentBasketSelector,
  ) ?? {
    displayBackofficeSharedBasket: undefined,
  };
  const { share, publicShareAuthorized } = useBasketShare(
    displayBackofficeSharedBasket ?? false,
    basket.id,
    sentryCallback,
    snackbarCallback,
  );
  const { hideShareBasketAction, elementImageSize } = useConfiguration<Basket>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_BASKET,
  ) ?? { hideShareBasketAction: undefined, elementImageSize: undefined };

  const { t } = useTranslation();
  const selectedElementIds = useRecoilValue(selectedElements(DataViewKey.BASKET_DETAIL));
  // Force page to 1 because the button will download all the basket
  const download = useBasketDownload(DataViewKey.BASKET_DETAIL, selectedElementIds, 1, basket.id);

  const handleActionMenuClick = (action: ActionValue) => {
    switch (action) {
      case ActionValue.DELETE_BASKET:
        setOpenDelete(true);
        break;
      case ActionValue.EDIT_BASKET:
        setOpenEdit(true);
        setTitleEdit(basket.title);
        break;
      case ActionValue.SHARE:
        share();
        break;
      case ActionValue.DOWNLOAD:
        download();
        break;
      default:
        logger.warn(
          "BasketCardForWidget - handleActionMenuClick ActionValue not yet supported = ",
          action,
        );
        break;
    }
  };

  const routeToBasket = () => {
    // Reset to first page while openning a basket
    setPage(1);
    router.push(PagesPath.BASKET_ELEMENT_PAGE.replace("[basketId]", String(basket.id)));
  };

  return (
    <Fragment>
      <BasketCard
        onClick={() => {
          routeToBasket();
        }}
        KIcon={KIcon}
        image={basket.media}
        title={basket.title}
        count={basket.mediaCount}
        isSelected={isSelected}
        allowSelection={true}
        onSelect={toggle}
        backgroundSize={
          elementImageSize ? BasketCardBackgroundSize.Contain : BasketCardBackgroundSize.Cover
        }
        actionsArea={
          <Box
            sx={{
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2),
              display: "flex",
              flexDirection: "row",
            }}
            className={clsx("BasketCard-ActionsArea")}
          >
            <Box sx={{ flexGrow: 1, display: "flex", flex: 1 }}>
              <KButton
                onClick={() => handleActionMenuClick(ActionValue.DELETE_BASKET)}
                sx={{ minWidth: "unset" }}
                aria-label={t("basketWidgetList.element.action.delete", {
                  basketTitle: basket.title,
                })}
                title={t("basketWidgetList.element.action.delete", {
                  basketTitle: basket.title,
                })}
                buttonName={ActionValue.DELETE_BASKET}
              >
                <KIcon fontSize="small" className={"fa-solid fa-trash"} />
              </KButton>
            </Box>
            <Box sx={{ display: "flex", flex: 1, justifyContent: "end" }}>
              <KButton
                onClick={() => handleActionMenuClick(ActionValue.DOWNLOAD)}
                sx={{ minWidth: "unset" }}
                disabled={basket.mediaCount <= 0}
                aria-label={t("basketWidgetList.element.action.download")}
                title={t("basketWidgetList.element.action.download")}
                buttonName={ActionValue.DOWNLOAD}
              >
                <KIcon fontSize="small" className="fa-solid fa-cloud-arrow-down" />
              </KButton>
              <KButton
                onClick={() => {
                  routeToBasket();
                }}
                sx={{ minWidth: "unset" }}
                aria-label={t("basketWidgetList.element.action.go-to-basket")}
                title={t("basketWidgetList.element.action.go-to-basket")}
                buttonName={"goToBasket"}
              >
                <KIcon fontSize="small" className="fa-solid fa-eye" />
              </KButton>
              <KButton
                onClick={() => handleActionMenuClick(ActionValue.EDIT_BASKET)}
                sx={{ minWidth: "unset" }}
                aria-label={t("basketWidgetList.element.action.edit", {
                  basketTitle: basket.title,
                })}
                title={t("basketWidgetList.element.action.edit", {
                  basketTitle: basket.title,
                })}
                buttonName={ActionValue.EDIT_BASKET}
              >
                <KIcon fontSize="small" className={"fa-solid fa-pen"} />
              </KButton>
            </Box>
            {publicShareAuthorized && !hideShareBasketAction && (
              <Box>
                <KButton
                  onClick={() => handleActionMenuClick(ActionValue.SHARE)}
                  size="small"
                  variant="contained"
                  sx={{ paddingLeft: 1, paddingRight: 1, minWidth: 0 }}
                  disabled={basket.mediaCount <= 0}
                  aria-label={t("basketWidgetList.element.action.share")}
                  title={t("basketWidgetList.element.action.share")}
                  buttonName={ActionValue.SHARE}
                >
                  {t("media-detail.action.share.label")}
                </KButton>
              </Box>
            )}
          </Box>
        }
      />
      <BasketDelete
        open={openDelete}
        close={() => setOpenDelete(false)}
        currentBasketTitle={basket.title}
        deleteBasket={deleteBasket}
        numberOfBaskets={1}
      />
      <BasketEdit
        title={titleEdit}
        setTitle={setTitleEdit}
        update={() => update({ title: titleEdit })}
        open={openEdit}
        close={() => setOpenEdit(false)}
      />
      <BasketShare
        basketId={basket.id}
        basketTitle={basket.title}
        sentryCallback={sentryCallback}
        snackbarCallback={snackbarCallback}
      />
    </Fragment>
  );
};

export default withSuspense(BasketCardForWidget);
